import { Dispatch, SetStateAction } from "react";
import { Table, Input, Radio } from "semantic-ui-react";
import { coreCountries, ITableValues } from "../../../../../models/npShifts";
import { INPShift } from "../../../../../store/npShiftsReducer";

interface IProps {
    selectedNPShift: INPShift;
    setSelectedNPShift: Dispatch<SetStateAction<INPShift | undefined>>;
    newTotals: ITableValues;
    setNewTotals: Dispatch<SetStateAction<ITableValues>>;
    refProgs: ITableValues;
    setRefProgs: Dispatch<SetStateAction<ITableValues>>;
    hasExchangeData: ITableValues;
    npCores: ITableValues;
    setNpCores: Dispatch<SetStateAction<ITableValues>>;
    npNonCores: ITableValues;
    setNpNonCores: Dispatch<SetStateAction<ITableValues>>;
}

const missingRefProgClass = (isMissing: boolean) =>
    `content-cell${isMissing ? " npshift-col-missing-refprog" : ""}`;

export const NPShiftTable = ({
    selectedNPShift,
    setSelectedNPShift,
    newTotals,
    setNewTotals,
    refProgs,
    setRefProgs,
    hasExchangeData,
    npCores,
    setNpCores,
    npNonCores,
    setNpNonCores,
}: IProps) => {
    const handleNPValueChange = (country: string, value: number) => {
        setSelectedNPShift({
            ...selectedNPShift,
            values: {
                ...selectedNPShift.values,
                [country]: {
                    ...selectedNPShift.values[country],
                    value: value,
                },
            },
        });
        setNewTotals({ ...newTotals, [country]: +refProgs[country] + +value });
    };

    const handleRefProgChange = (country: string, value: number) => {
        const nextRefProgs = { ...refProgs, [country]: value };
        setRefProgs(nextRefProgs);
        setNewTotals({ ...newTotals, [country]: +value + (selectedNPShift.values[country]?.value ?? 0) });
    };

    const handleNpCoreChange = (country: string, value: number) => {
        setNpCores({ ...npCores, [country]: value });
    };

    const handleNpNonCoreChange = (country: string, value: number) => {
        setNpNonCores({ ...npNonCores, [country]: value });
    };

    const handleNPShiftChange = (country: string, bznShift: number) => {
        const newBznShift = bznShift === 1 ? 0 : 1;
        setSelectedNPShift({
            ...selectedNPShift,
            values: {
                ...selectedNPShift.values,
                [country]: {
                    ...selectedNPShift.values[country],
                    bznShift: newBznShift,
                },
            },
        });
    };

    return (
        <Table id="table-NPShiftTable-a" celled>
            <Table.Header id="tableHeader-NPShiftTable-a" >
                <Table.Row id="tableRow-NPShiftTable-a">
                    <Table.HeaderCell id="tableHeaderCell-NPShiftTable-valueType">Value type [MW]</Table.HeaderCell>
                    {Object.keys(selectedNPShift.values).map(x => (
                        <Table.HeaderCell
                            id={`tableHeaderCell-NPShiftTable-${x}`}
                            key={x}
                            className={missingRefProgClass(hasExchangeData[x] !== 1)}
                        >
                            {x.toUpperCase()}
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>
            <Table.Body id="tableBody-NPShiftTable-b">
                <Table.Row id="tableRow-NPShiftTable-b">
                    <Table.Cell id="tableCell-NPShiftTable-refProg" collapsing>RefProg</Table.Cell>
                    {Object.keys(selectedNPShift.values).map((country, index) => {
                        const isMissingInRefProg = hasExchangeData[country] !== 1;
                        return (
                            <Table.Cell
                                id={`tablecell-NPShiftTable-c-${index}`}
                                key={country}
                                className={missingRefProgClass(isMissingInRefProg)}
                                selectable={isMissingInRefProg}
                            >
                                {isMissingInRefProg ? (
                                    <Input
                                        id={`input-NPShiftTable-refProg-${index}`}
                                        type="number"
                                        pattern="[0-9]*"
                                        value={refProgs[country] ?? ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleRefProgChange(country, parseFloat(e.target.value) || 0)
                                        }
                                    />
                                ) : (
                                    parseFloat((refProgs[country] || 0).toFixed(2))
                                )}
                            </Table.Cell>
                        );
                    })}
                </Table.Row>
                <Table.Row id="tableRow-NPShiftTable-d">
                    <Table.Cell id="tableCell-NPShiftTable-NPCore" collapsing>NP Core</Table.Cell>
                    {Object.keys(selectedNPShift.values).map((country, index2) => {
                        const isMissingInRefProg = hasExchangeData[country] !== 1;
                        return (
                            <Table.Cell
                                id={`tablecell-NPShiftTable-d-${index2}`}
                                key={country}
                                className={missingRefProgClass(isMissingInRefProg)}
                                selectable={isMissingInRefProg}
                            >
                                {isMissingInRefProg ? (
                                    <Input
                                        id={`input-NPShiftTable-npCore-${index2}`}
                                        type="number"
                                        pattern="[0-9]*"
                                        value={npCores[country] ?? ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleNpCoreChange(country, parseFloat(e.target.value) || 0)
                                        }
                                    />
                                ) : (
                                    parseFloat((npCores[country] || 0).toFixed(2))
                                )}
                            </Table.Cell>
                        );
                    })}
                </Table.Row>
                <Table.Row id="tableRow-NPShiftTable-e">
                    <Table.Cell id="tableCell-NPShiftTable-NPNonCore" collapsing>NP Non-Core</Table.Cell>
                    {Object.keys(selectedNPShift.values).map((country, index3) => {
                        const isMissingInRefProg = hasExchangeData[country] !== 1;
                        return (
                            <Table.Cell
                                id={`tablecell-NPShiftTable-e-${index3}`}
                                key={country}
                                className={missingRefProgClass(isMissingInRefProg)}
                                selectable={isMissingInRefProg}
                            >
                                {isMissingInRefProg ? (
                                    <Input
                                        id={`input-NPShiftTable-npNonCore-${index3}`}
                                        type="number"
                                        pattern="[0-9]*"
                                        value={npNonCores[country] ?? ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleNpNonCoreChange(country, parseFloat(e.target.value) || 0)
                                        }
                                    />
                                ) : (
                                    parseFloat((npNonCores[country] || 0).toFixed(2))
                                )}
                            </Table.Cell>
                        );
                    })}
                </Table.Row>
                <Table.Row id="tableRow-NPShiftTable-f">
                    <Table.Cell id="tableCell-NPShiftTable-NPshift" collapsing>NP shift</Table.Cell>
                    {Object.entries(selectedNPShift.values).map((nps, index4) => {
                        const missingInRefProg = hasExchangeData[nps[0]] !== 1;
                        return (
                            <Table.Cell
                                id={`tablecell-NPShiftTable-f-${index4}`}
                                selectable
                                key={nps[0]}
                                className={missingRefProgClass(missingInRefProg)}
                            >
                                <Input
                                    id={`input-NPShiftTable-f-${index4}`}
                                    type="number"
                                    pattern="[0-9]*"
                                    value={nps[1].value}
                                    onChange={(e: any) => handleNPValueChange(nps[0], e.target.value)}
                                />
                            </Table.Cell>
                        );
                    })}
                </Table.Row>
                <Table.Row id="tableRow-NPShiftTable-g">
                    <Table.Cell id="tableCell-NPShiftTable-countryGSK" collapsing>Country GSK</Table.Cell>
                    {Object.entries(selectedNPShift.values).map((np, index5) => {
                        const isInRefProg = hasExchangeData[np[0]] === 1;
                        const canToggle = coreCountries.includes(np[0]) || !isInRefProg;
                        return (
                            <Table.Cell
                                id={`tablecell-NPShiftTable-g-${index5}`}
                                selectable
                                key={np[0]}
                                className={missingRefProgClass(!isInRefProg)}
                            >
                                <Radio
                                    disabled={!canToggle}
                                    toggle
                                    checked={(isInRefProg && np[1].bznShift === 1) || np[1].bznShift === 1}
                                    onChange={() => handleNPShiftChange(np[0], np[1].bznShift)}
                                />
                            </Table.Cell>
                        );
                    })}
                </Table.Row>
                <Table.Row id="tableRow-NPShiftTable-h">
                    <Table.Cell id="tableCell-NPShiftTable-newTotal" collapsing>New Total</Table.Cell>
                    {Object.keys(selectedNPShift.values).map((country, index6) => (
                        <Table.Cell
                            id={`tablecell-NPShiftTable-h-${index6}`}
                            key={country}
                            className={missingRefProgClass(hasExchangeData[country] !== 1)}
                        >
                            {parseFloat((newTotals[country] || 0).toFixed(2))}
                        </Table.Cell>
                    ))}
                </Table.Row>
            </Table.Body>
        </Table>
    );
};
